import type { AxiosResponse } from 'axios'

import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'
import type { DiscountItemLine } from '@/modules/inventory/discounts/types'
import type { AdjustmentBatchRow } from '@/services/inventoryAdjustmentService'

export type DiscountLinePayload = {
  id: number
  discountId: number
  itemId: number
  batchId: number
  discountPercent: number
  isDeleted: string
}

export type DiscountSavePayload = {
  id: number
  code: string
  date: string
  schemeStartDate: string
  schemeEndDate: string
  branchId: number
  itemList: DiscountLinePayload[]
}

/** Map HTML date (yyyy-mm-dd) to API ISO string. */
export function discountDateInputToApiIso(dateStr: string): string {
  const s = (dateStr || '').trim()
  if (!s) return new Date().toISOString()
  if (s.includes('T')) {
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString()
  }
  return `${s}T12:00:00.000Z`
}

function unwrap(payload: unknown): unknown {
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if ('success' in p && 'data' in p) return p.data
    if ('data' in p) return p.data
  }
  return payload
}

function toArray(payload: unknown): any[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
    return (raw as { data: any[] }).data
  }
  if (raw && typeof raw === 'object') return [raw]
  return []
}

export type DiscountItemOption = { value: string; label: string }

function mapAvailableItem(x: Record<string, unknown>): DiscountItemOption | null {
  const id = Number(x?.itemId ?? x?.ItemId ?? x?.id ?? x?.Id ?? 0)
  if (!Number.isFinite(id) || id <= 0) return null
  const code = String(x?.itemCode ?? x?.ItemCode ?? x?.code ?? x?.Code ?? '').trim()
  const nameEn = String(x?.itemNameEn ?? x?.ItemNameEn ?? x?.nameEn ?? x?.NameEn ?? '').trim()
  const nameAr = String(x?.itemNameAr ?? x?.ItemNameAr ?? x?.nameAr ?? x?.NameAr ?? '').trim()
  const name = String(x?.name ?? x?.Name ?? '').trim()
  const label =
    [code, nameEn || nameAr || name].filter(Boolean).join(' — ').trim() || String(id)
  return { value: String(id), label }
}

function mapAvailableBatch(x: Record<string, unknown>): AdjustmentBatchRow | null {
  const id = Number(x?.batchId ?? x?.BatchId ?? x?.id ?? x?.Id ?? 0)
  if (!Number.isFinite(id) || id <= 0) return null
  const code = String(x?.code ?? x?.Code ?? x?.batchCode ?? x?.BatchCode ?? '').trim()
  const reference = String(x?.reference ?? x?.Reference ?? '').trim()
  const availableQuantity = Number(
    x?.availableQuantity ?? x?.AvailableQuantity ?? x?.quantity ?? x?.Quantity ?? 0,
  )
  return {
    id,
    code: code || reference || String(id),
    reference,
    availableQuantity: Number.isFinite(availableQuantity) ? availableQuantity : 0,
  }
}

export async function getDiscountAvailableItems(branchId: number): Promise<DiscountItemOption[]> {
  const url = endpoints.inventoryDiscount.getAvailableItems(branchId)
  const response = await apiClient.get<ApiResponse<any> | any>(url)
  const arr = toArray(response.data)
  const out: DiscountItemOption[] = []
  for (const row of arr) {
    const opt = mapAvailableItem(row && typeof row === 'object' ? (row as Record<string, unknown>) : {})
    if (opt) out.push(opt)
  }
  return out
}

export async function getDiscountAvailableBatches(
  branchId: number,
  itemId: number,
): Promise<AdjustmentBatchRow[]> {
  const url = endpoints.inventoryDiscount.getAvailableBatches(branchId, itemId)
  const response = await apiClient.get<ApiResponse<any> | any>(url)
  const arr = toArray(response.data)
  const out: AdjustmentBatchRow[] = []
  for (const row of arr) {
    const b = mapAvailableBatch(row && typeof row === 'object' ? (row as Record<string, unknown>) : {})
    if (b) out.push(b)
  }
  return out
}

export async function saveDiscount(payload: DiscountSavePayload): Promise<AxiosResponse<unknown>> {
  return apiClient.post<unknown>(endpoints.inventoryDiscount.save, payload)
}

export async function updateDiscount(payload: DiscountSavePayload): Promise<AxiosResponse<unknown>> {
  return apiClient.put<unknown>(endpoints.inventoryDiscount.update, payload)
}

export function unwrapDiscountResponseBody(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const inner = o.data ?? o.Data
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return o
}

export function extractDiscountMasterIdFromResponse(raw: unknown): number {
  const p = unwrapDiscountResponseBody(raw)
  if (!p) return 0
  const id = Number(p.id ?? p.Id ?? p.discountId ?? p.DiscountId ?? 0)
  return Number.isFinite(id) && id > 0 ? id : 0
}

// --- List / detail ---

export type DiscountListRow = {
  id: string
  serverDiscountId: number
  code: string
  date: string
  startDate: string
  endDate: string
}

export type DiscountMasterVm = {
  id: number
  code: string
  date: string
  schemeStartDate: string
  schemeEndDate: string
  branchId: number
}

function dateToInputValue(raw: unknown): string {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

function mapDiscountListRow(x: Record<string, unknown>): DiscountListRow {
  const serverDiscountId = Number(x.id ?? x.Id ?? 0)
  return {
    id: serverDiscountId > 0 ? String(serverDiscountId) : `tmp-${Math.random().toString(36).slice(2, 9)}`,
    serverDiscountId,
    code: String(x.code ?? x.Code ?? ''),
    date: dateToInputValue(x.date ?? x.Date),
    startDate: dateToInputValue(
      x.schemeStartDate ?? x.SchemeStartDate ?? x.startDate ?? x.StartDate,
    ),
    endDate: dateToInputValue(x.schemeEndDate ?? x.SchemeEndDate ?? x.endDate ?? x.EndDate),
  }
}

function pickDiscountMasterRow(payload: unknown): Record<string, unknown> {
  const raw = unwrap(payload)
  if (Array.isArray(raw) && raw[0] && typeof raw[0] === 'object') {
    return raw[0] as Record<string, unknown>
  }
  if (raw && typeof raw === 'object') {
    const p = raw as Record<string, unknown>
    const inner = p.data ?? p.Data
    if (Array.isArray(inner) && inner[0] && typeof inner[0] === 'object') {
      return inner[0] as Record<string, unknown>
    }
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      return inner as Record<string, unknown>
    }
    return p
  }
  return {}
}

export function mapDiscountMasterVm(row: Record<string, unknown>): DiscountMasterVm {
  return {
    id: Number(row.id ?? row.Id ?? 0),
    code: String(row.code ?? row.Code ?? ''),
    date: dateToInputValue(row.date ?? row.Date),
    schemeStartDate: dateToInputValue(
      row.schemeStartDate ?? row.SchemeStartDate ?? row.startDate ?? row.StartDate,
    ),
    schemeEndDate: dateToInputValue(
      row.schemeEndDate ?? row.SchemeEndDate ?? row.endDate ?? row.EndDate,
    ),
    branchId: Number(row.branchId ?? row.BranchId ?? 0),
  }
}

function newLineReactId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `ln-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function mapApiDiscountLineToFormLine(x: Record<string, unknown>, reactKey: string): DiscountItemLine {
  const sid = Number(x.id ?? x.Id ?? 0)
  const itemId = Number(x.itemId ?? x.ItemId ?? 0)
  const batchId = Number(x.batchId ?? x.BatchId ?? 0)
  const pct = x.discountPercent ?? x.DiscountPercent
  return {
    id: reactKey,
    serverLineId: sid > 0 ? sid : undefined,
    itemId: itemId > 0 ? String(itemId) : '',
    batchId: itemId > 0 ? String(Number.isFinite(batchId) && batchId > 0 ? batchId : 0) : '',
    discountPercent: pct != null && String(pct).trim() !== '' ? String(pct) : '',
  }
}

export async function getAllDiscounts(
  branchId: number,
  pageNumber: number,
  pageSize: number,
): Promise<{ items: DiscountListRow[]; totalCount: number }> {
  const url = endpoints.inventoryDiscount.getAll(branchId, pageNumber, pageSize)
  const response = await apiClient.get<ApiResponse<unknown> | unknown>(url)
  const payload = unwrap(response.data)

  let itemsRaw: unknown[] = []
  let totalCount = 0

  if (Array.isArray(payload)) {
    itemsRaw = payload
    totalCount = payload.length
  } else if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    const data = p.data ?? p.Data ?? p.items ?? p.Items
    if (Array.isArray(data)) itemsRaw = data
    totalCount = Number(
      p.totalCount ??
        p.TotalCount ??
        p.totalRecords ??
        p.TotalRecords ??
        p.total ??
        p.Total ??
        itemsRaw.length,
    )
    if (!Number.isFinite(totalCount)) totalCount = itemsRaw.length
  }

  const items = itemsRaw.map((row) =>
    mapDiscountListRow(row && typeof row === 'object' ? (row as Record<string, unknown>) : {}),
  )
  return { items, totalCount }
}

export async function getDiscountMasterById(branchId: number, id: number): Promise<DiscountMasterVm> {
  const url = endpoints.inventoryDiscount.getById(branchId, id)
  const response = await apiClient.get<ApiResponse<unknown> | unknown>(url)
  const row = pickDiscountMasterRow(response.data)
  return mapDiscountMasterVm(row)
}

export async function deleteDiscount(branchId: number, id: number): Promise<void> {
  await apiClient.delete(endpoints.inventoryDiscount.delete(branchId, id))
}

export async function getDiscountLinesByMasterId(
  branchId: number,
  masterId: number,
): Promise<DiscountItemLine[]> {
  const url = endpoints.inventoryDiscount.getLinesByMasterId(branchId, masterId)
  const response = await apiClient.get<ApiResponse<any> | any>(url)
  const arr = toArray(response.data)
  return arr.map((row) =>
    mapApiDiscountLineToFormLine(
      row && typeof row === 'object' ? (row as Record<string, unknown>) : {},
      newLineReactId(),
    ),
  )
}

export const discountService = {
  getAvailableItems: getDiscountAvailableItems,
  getAvailableBatches: getDiscountAvailableBatches,
  save: saveDiscount,
  update: updateDiscount,
  delete: deleteDiscount,
  getAll: getAllDiscounts,
  getById: getDiscountMasterById,
  getLinesByMasterId: getDiscountLinesByMasterId,
}
